import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { differenceInCalendarDays, parseISO } from 'date-fns'
import { AlarmClock, CalendarRange, Clock, Inbox, Search, X } from 'lucide-react'
import { DateValue, EmptyState, ErrorState, KpiGrid, PageHeader, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { usePendingTimesheets } from '../api'

/** A week submitted this long ago without a decision is somebody being chased. */
const OVERDUE_DAYS = 7

function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('')
}

function daysWaiting(submittedAt: string | null): number | null {
  return submittedAt ? differenceInCalendarDays(new Date(), parseISO(submittedAt)) : null
}

/**
 * Every timesheet waiting on somebody.
 *
 * <p>Beside the approver's own inbox, not instead of it: the inbox is what one manager acts
 * on, and this is what the person chasing a late week needs to see. It uses the same
 * `timesheet:approve` permission — row-level security already limits it to the entities the
 * viewer holds, so the two differ in scope rather than in authority.
 */
export function TimesheetApprovalsPage() {
  useBreadcrumb()
  const pending = usePendingTimesheets()
  const [search, setSearch] = useState('')

  // Oldest first: the week that has waited longest is the one to chase.
  const sheets = useMemo(
    () => [...(pending.data ?? [])].sort((left, right) =>
      (left.submittedAt ?? '').localeCompare(right.submittedAt ?? '')),
    [pending.data],
  )

  const needle = search.trim().toLowerCase()
  const rows = needle
    ? sheets.filter((sheet) =>
      `${sheet.employeeName ?? ''} ${sheet.employeeCode ?? ''}`.toLowerCase().includes(needle))
    : sheets

  const totalHours = sheets.reduce((sum, sheet) => sum + sheet.totalHours, 0)
  const overdue = sheets.filter((sheet) => (daysWaiting(sheet.submittedAt) ?? 0) >= OVERDUE_DAYS).length
  const people = new Set(sheets.map((sheet) => sheet.employeeId)).size

  return (
    <div className="space-y-5">
      <PageHeader
        title="Timesheet approvals"
        subtitle="Everything submitted and waiting, across the entities you hold."
        actions={
          <Button asChild variant="outline">
            <Link to="/approvals"><Inbox aria-hidden="true" /> Open my inbox</Link>
          </Button>
        }
      />

      {pending.isPending && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((index) => <Skeleton key={index} className="h-[104px] rounded-[var(--radius-card)]" />)}
        </div>
      )}
      {pending.isSuccess && sheets.length > 0 && (
        <KpiGrid
          items={[
            { label: 'Weeks waiting', value: sheets.length, icon: CalendarRange, tint: 'purple' },
            { label: 'People', value: people, icon: Inbox, tint: 'blue' },
            { label: 'Hours awaiting sign-off', value: totalHours.toLocaleString(), icon: Clock, tint: 'green' },
            {
              label: `Waiting ${OVERDUE_DAYS}+ days`,
              value: overdue,
              icon: AlarmClock,
              tint: overdue > 0 ? 'red' : 'green',
              caption: overdue > 0 ? 'Worth a nudge' : 'Nothing is stuck',
            },
          ]}
        />
      )}

      <Card className="overflow-hidden">
        {pending.isSuccess && sheets.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--color-border)] p-4">
            <div className="relative w-full sm:w-72">
              <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-[var(--color-muted)]" aria-hidden="true" />
              <Input
                aria-label="Search people"
                placeholder="Search by name or code"
                className="ps-9 pe-9"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              {search && (
                <button
                  type="button"
                  aria-label="Clear search"
                  className="absolute end-2 top-1/2 grid size-6 -translate-y-1/2 place-items-center rounded text-[var(--color-muted)] hover:text-[var(--color-heading)]"
                  onClick={() => setSearch('')}
                >
                  <X className="size-4" aria-hidden="true" />
                </button>
              )}
            </div>
            <p className="text-xs text-[var(--color-muted)]">
              {rows.length === sheets.length
                ? `${sheets.length} week${sheets.length === 1 ? '' : 's'}, oldest first`
                : `${rows.length} of ${sheets.length} weeks`}
            </p>
          </div>
        )}

        {pending.isPending && (
          <div className="space-y-3 p-4">
            {[0, 1, 2].map((index) => <Skeleton key={index} className="h-10" />)}
          </div>
        )}
        {pending.isError && (
          <ErrorState error={pending.error} onRetry={() => void pending.refetch()} />
        )}
        {pending.isSuccess && sheets.length === 0 && (
          <EmptyState
            icon={Inbox}
            title="Nothing is waiting"
            description="Every submitted week has been decided. Weeks still in draft are with the people who own them, and do not appear here."
          />
        )}
        {pending.isSuccess && sheets.length > 0 && rows.length === 0 && (
          <EmptyState
            icon={Search}
            title="No one matches that search"
            description="Try part of a name or an employee code."
            action={<Button variant="outline" size="sm" onClick={() => setSearch('')}>Clear search</Button>}
          />
        )}
        {rows.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full min-w-[40rem] text-sm">
              <thead>
                <tr>
                  <th scope="col" className="px-4 py-2.5 text-start">Person</th>
                  <th scope="col" className="px-4 py-2.5 text-start">Week</th>
                  <th scope="col" className="px-4 py-2.5 text-end">Hours</th>
                  <th scope="col" className="px-4 py-2.5 text-start">Submitted</th>
                  <th scope="col" className="px-4 py-2.5 text-start">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((sheet) => {
                  const name = sheet.employeeName ?? sheet.employeeCode ?? 'Unknown'
                  const waited = daysWaiting(sheet.submittedAt)
                  const late = waited != null && waited >= OVERDUE_DAYS
                  return (
                    <tr key={sheet.id} className="border-t border-[var(--color-border)]">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className="grid size-9 shrink-0 place-items-center rounded-full bg-[var(--color-primary-soft)] text-xs font-semibold text-[var(--color-primary)]">
                            {initials(name)}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate font-medium text-[var(--color-heading)]">{name}</p>
                            {sheet.employeeCode && (
                              <p className="font-mono text-xs text-[var(--color-muted)]">{sheet.employeeCode}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[var(--color-body)]">
                        <DateValue value={sheet.weekStart} /> – <DateValue value={sheet.weekEnd} />
                      </td>
                      <td className="px-4 py-3 text-end font-semibold tabular-nums text-[var(--color-heading)]">
                        {sheet.totalHours}
                      </td>
                      <td className="px-4 py-3">
                        {sheet.submittedAt ? (
                          <span className={late ? 'font-medium text-[var(--color-danger)]' : 'text-[var(--color-body)]'}>
                            <DateValue value={sheet.submittedAt} relative />
                          </span>
                        ) : <span className="text-[var(--color-muted)]">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {late
                          ? <StatusBadge status="OVERDUE" tone="danger" label="Overdue" />
                          : <StatusBadge status={sheet.status} />}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
